/** @file escapeLadder: one Esc takes one rung: popover or menu, dialog, drag, (leave Settings), selection. */
import type { EditorStore } from '../state/editorTypes';
import { tileDrafts } from '../screens/tile/tileDrafts';
import { cancelDragEvent } from './cancelDragEvent';


/** The attribute a popover's full-window backdrop carries; the ladder clicks the last one (the top-most) to close it. */
const POPOVER = '[data-popover-backdrop]';

/** Rung 1: the open context menu, the menu-bar menu, or the top-most popover. */
function closePopover(store: EditorStore): boolean {
  const s = store.getState();
  if (s.menu !== null) { s.closeMenu(); return true; }
  if (s.shell.menuBar !== null) { s.setShell({ menuBar: null }); return true; }
  const top = [...document.querySelectorAll<HTMLElement>(POPOVER)].at(-1);
  if (top === undefined) return false;
  top.click();
  return true;
}

/** Rung 3: a drag in flight shows its pose through `poseOverride`; clearing it restores the pre-drag pose. */
function cancelDrag(store: EditorStore): boolean {
  const s = store.getState();
  if (s.poseOverride === null) return false;
  s.setPoseOverride(null);
  globalThis.dispatchEvent(new Event(cancelDragEvent));
  return true;
}

/** Rung 4 on a Tile tab: its open prompt (unsaved close, image fit) is cancelled; then the ladder stops there. */
function tilePrompt(): boolean {
  const drafts = tileDrafts.getState();
  if (drafts.prompt === null) return false;
  drafts.setPrompt(null);
  return true;
}

/** Rung 4: leave Settings for the open puppet (or the projects list). */
function leaveSettings(store: EditorStore): boolean {
  const s = store.getState();
  if (s.route.screen !== 'settings') return false;
  s.navigate(s.puppet.id === null ? { screen: 'projects' } : { screen: 'pose', puppetId: s.puppet.id });
  return true;
}

/** Rung 5: clear what is selected (nodes, drawable, slot, timeline keys). */
function clearSelection(store: EditorStore): boolean {
  const s = store.getState();
  const sel = s.selection;
  if (sel.nodes.length === 0 && sel.drawable === null && sel.slot === null && sel.keys.length === 0) return false;
  s.select({ nodes: [], drawable: null, slot: null, keys: [] });
  return true;
}

/**
 * The shell's Escape: the first rung that has something to do, once, top to bottom: close the top-most popover or menu;
 * close the dialog; cancel an active drag; leave Settings; clear the selection. On a Tile tab the rungs after the
 * dialog are only the tab's prompt (Esc never closes the tab or clears the Pose selection there). Returns whether a
 * rung was taken.
 */
export function escapeLadder(store: EditorStore): boolean {
  const dialog = (st: EditorStore): boolean => { const s = st.getState(); if (s.shell.dialog === null) return false; s.setShell({ dialog: null }); return true; };
  const rest = store.getState().route.screen === 'tile' ? [tilePrompt] : [cancelDrag, leaveSettings, clearSelection];
  return [closePopover, dialog, ...rest].some((rung) => rung(store));
}
